/**
 * Landing page copy. Single source of truth — every landing section reads
 * strings from here so marketing can edit copy without touching component
 * code. Types are locked so a typo in a key breaks the build, not the page.
 *
 * Voice rules (LANDING-01 planning conversation):
 *   - Talk about their pain, not our tech.
 *   - Concrete numbers > adjectives.
 *   - No jargon: no "workflow orchestration", "serverless", "MCP", "SDK",
 *     "on-prem", "hybrid", "sovereign", "edge compute", "observability".
 *   - One promise, one page, one primary CTA.
 *
 * Placeholders for v1 (swap in real assets without touching TSX):
 *   - PROOF.testimonial (currently null — box hides until we have a quote)
 *   - DEMO.videoUrl (currently null — placeholder card shows instead)
 *   - FOUNDERS[].photoUrl (currently null — initial-avatar shows instead)
 */

export interface CtaLink {
  label: string;
  href: string;
}

export const HERO = {
  eyebrow: 'Private infrastructure · On your hardware',
  headline: 'Run the cloud inside your own building.',
  headlineTail: 'Your servers. Your data. Your rules.',
  subhead:
    'Vextura gives you workflow automation, code execution, and file storage — installed on your servers. Same platform, same API, one command to install.',
  primaryCta: { label: 'Deploy on-premises', href: '/signup?deploy=onprem' },
  secondaryCta: { label: 'See pricing', href: '#pricing' },
  trustLine: 'Live at QazPost — 350 TPS in production.',
} as const;

export interface PainPoint {
  title: string;
  body: string;
}

export const PAIN_POINTS: readonly PainPoint[] = [
  {
    title: 'Your service chokes at 25 TPS.',
    body: "Adding hardware doesn't fix it. Adding engineers doesn't either. The bottleneck is architecture, not budget.",
  },
  {
    title: 'Every change takes 2 months and 4 vendors.',
    body: 'By the time it ships, the requirement has already changed. Compliance signed off on last quarter, not this one.',
  },
  {
    title: 'Every new source is another integration.',
    body: 'The vendor list grows. The dashboard count grows. Nobody sees the whole picture in one place.',
  },
] as const;

export interface BeforeAfterRow {
  before: string;
  after: string;
}

export const BEFORE_AFTER: {
  headline: string;
  rows: readonly BeforeAfterRow[];
} = {
  headline: 'Same team. Same hardware. Different numbers.',
  rows: [
    { before: '25 TPS peak throughput', after: '350 TPS — 14× more, same hardware' },
    { before: '2 months to roll out a process change', after: '1 day' },
    { before: '5 vendors stitched together', after: '1 platform, one dashboard' },
    { before: 'Every source is a bespoke integration', after: 'Any source, handled the same way' },
  ],
} as const;

export interface ProofStat {
  value: string;
  caption: string;
}

export const PROOF = {
  headline: 'One customer. Real production. Real numbers.',
  stats: [
    { value: '350 TPS', caption: 'In production at QazPost — from 25 TPS on the same endpoint.' },
    { value: '1 day', caption: 'To roll out a process change. It used to take two months.' },
    { value: '1 platform', caption: 'Replaced five vendors — one dashboard, one contract.' },
  ] as readonly ProofStat[],
  story:
    'QazPost — a national postal bank — runs fraud detection and incident management across every payment source on Vextura. It has been in production since 2026.',
  logos: [{ name: 'QazPost', href: 'https://qazpost.kz' }] as readonly {
    name: string;
    href?: string;
  }[],
  testimonial: null as null | { quote: string; author: string; org: string },
} as const;

export const DEMO = {
  eyebrow: '30 seconds',
  headline: 'See it work.',
  subhead: 'A silent loop of the console doing something useful. No voice-over, no reading.',
  videoUrl: null as string | null, // placeholder card renders until we have a real URL
} as const;

export interface PricingTier {
  name: string;
  price: string;
  priceSuffix?: string;
  tagline: string;
  features: readonly string[];
  cta: CtaLink;
  highlighted?: boolean;
}

export const PRICING_ONPREM: {
  headline: string;
  subhead: string;
  tiers: readonly PricingTier[];
} = {
  headline: 'On-premises — installed on your servers. Your data never leaves.',
  subhead: 'For banks, hospitals, and regulated enterprises. All prices monthly.',
  tiers: [
    {
      name: 'Starter',
      price: '$5,000',
      priceSuffix: '/ month',
      tagline: 'Your first production workload on your own hardware.',
      features: [
        'Runs on Linux, bare metal, or private VM',
        '1 production cluster',
        'Email support · 99.5% SLA',
        'One command to install',
      ],
      cta: { label: 'Get Starter', href: '/signup?deploy=onprem&plan=starter' },
    },
    {
      name: 'Growth',
      price: '$10,000',
      priceSuffix: '/ month',
      tagline: '5+ workloads, 24/7 operations.',
      features: [
        'Multi-cluster',
        'Priority support · 99.9% SLA',
        'Guided upgrades',
        'All Starter, unlocked',
      ],
      cta: { label: 'Get Growth', href: '/signup?deploy=onprem&plan=growth' },
      highlighted: true,
    },
    {
      name: 'Scale',
      price: '$15,000+',
      priceSuffix: '/ month',
      tagline: 'High volume, custom SLA, dedicated engineer.',
      features: [
        'Dedicated engineer',
        'Custom SLA',
        'Reserved capacity',
        'Compliance reports on request',
      ],
      cta: { label: 'Talk to us', href: '/signup?deploy=onprem&plan=scale' },
    },
  ],
} as const;

export interface Founder {
  name: string;
  title: string;
  bio: string;
  photoUrl: string | null;
}

export const FOUNDERS: {
  headline: string;
  subhead: string;
  founders: readonly Founder[];
} = {
  headline: 'Built by people who lived the problem.',
  subhead: 'Fifteen years of building infrastructure inside companies where the cloud was not an option.',
  founders: [
    {
      name: 'Khassangali Gapparov',
      title: 'Co-founder & CEO',
      bio: 'Senior software engineer. Ex-Apple, ex-Amazon. 3× CCIE. Fifteen years building infrastructure where "the cloud" was not an option — so we built the platform we always wished existed.',
      photoUrl: null,
    },
    {
      name: 'Kanat Omarov',
      title: 'Co-founder & CRO',
      bio: 'Serial founder — Baraiq, Asia Intercommunications, exclusive Cisco and Fortinet partner in the region. Runs go-to-market, sales, and enterprise relationships. Twenty years turning engineered products into signed customers.',
      photoUrl: null,
    },
  ],
} as const;

export const NOT_FOR_YOU = {
  headline: 'Not for you if…',
  subhead: 'We would rather tell you the truth than close the wrong deal.',
  bullets: [
    "You're happy with your AWS bill.",
    'Your data is fine in a public cloud.',
    'You already have a 20-person platform team.',
  ] as readonly string[],
} as const;

export const FINAL_CTA = {
  headline: 'Ready to run your own cloud?',
  subhead: 'One command to install. On your servers. Your data never leaves.',
  primaryCta: { label: 'Deploy on-premises', href: '/signup?deploy=onprem' },
  secondaryCta: { label: 'Talk to us', href: '/signup?deploy=onprem&plan=scale' },
} as const;

export const PUBLIC_NAV = {
  brand: 'Vextura',
  links: [
    { label: 'Pricing', href: '#pricing' },
    { label: 'Docs', href: 'https://docs.vextura.ai' },
  ] as readonly CtaLink[],
  signIn: { label: 'Sign in', href: '/signin' },
  signUp: { label: 'Create tenant', href: '/signup' },
} as const;

export const PUBLIC_FOOTER = {
  tagline: 'Vextura — the platform for teams whose data cannot leave the building.',
  linkGroups: [
    {
      heading: 'Product',
      links: [
        { label: 'Pricing', href: '#pricing' },
        { label: 'Docs', href: 'https://docs.vextura.ai' },
        { label: 'Status', href: 'https://status.vextura.ai' },
      ],
    },
    {
      heading: 'Company',
      links: [
        { label: 'About', href: '#founders' },
        { label: 'Contact', href: 'mailto:hello@vextura.ai' },
      ],
    },
    {
      heading: 'Legal',
      links: [
        { label: 'Terms', href: '/terms' },
        { label: 'Privacy', href: '/privacy' },
      ],
    },
  ] as readonly {
    heading: string;
    links: readonly CtaLink[];
  }[],
  copyright: '© 2026 Vextura, Inc.',
} as const;
